import { Outlet, useLocation } from "react-router-dom";
import { AppFooter } from "@/components/shell/AppFooter";
import { VersionUpdatePrompt } from "@/components/shell/VersionUpdatePrompt";
import { RouteTitle } from "./RouteTitle";

// =============================================================================
// AppLayout — wraps the routed pages with the "update available" version
// prompt (a centered modal that collapses to a corner banner on "Later").
// The header itself (logos, account menu, top actions)
// belongs to each individual page so the layout decisions about width
// and spacing stay local to that page.
//
// Renders a single visually-hidden "skip to main content" link at the
// very top so keyboard users can bypass the brand bar / navigation on
// every page. Each route's <main> element gets `id="main-content"` —
// hitting the skip link jumps focus there directly.
//
// The "Powered by Wisdom Weave · v{semver}" footer is rendered ONCE
// here so the version + attribution show on every route — the form,
// booked trips, AND every admin page — rather than only on the handful
// of pages that used to embed it themselves. The footer width matches
// each page's content card (max-w-[1100px]) so it stays aligned
// regardless of which page is mounted.
//
// The signed-out pages are the exception: they're full-height two-pane
// layouts (AuthLayout) that render their own footer pinned inside the
// form pane, so a global footer appended below the 100dvh layout would
// just sit off-screen.
// =============================================================================

const OWN_FOOTER = new Set([
  "/login",
  "/forgot-password",
  "/auth/magic",
  "/auth/invite",
  "/auth/reset-password",
]);

export function AppLayout() {
  const { pathname } = useLocation();
  const showGlobalFooter = !OWN_FOOTER.has(pathname);

  return (
    <>
      <RouteTitle />
      <a
        href="#main-content"
        className="sr-only focus-visible:not-sr-only focus-visible:fixed focus-visible:left-4 focus-visible:top-4 focus-visible:z-50 focus-visible:rounded-xl focus-visible:bg-primary focus-visible:px-4 focus-visible:py-2 focus-visible:text-sm focus-visible:font-bold focus-visible:text-primary-foreground focus-visible:shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Skip to main content
      </a>
      <Outlet />
      {showGlobalFooter ? (
        <div className="mx-auto w-full max-w-[1100px] px-4 pb-6">
          <AppFooter />
        </div>
      ) : null}
      <VersionUpdatePrompt />
    </>
  );
}
